// src/services/WebSocketService.js
// WebSocket service for real-time agent communication

import { getWebSocketUrl } from './apiConfig';

class WebSocketService {
  constructor() {
    this.ws = null;
    this.connectionId = null;
    this.userId = null;
    this.isConnecting = false;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second
    this.messageHandlers = new Map();
    this.connectionHandlers = new Set();
    this.errorHandlers = new Set();
    this.messageQueue = [];
    this.sessionId = null;
    this.heartbeatInterval = null;
    this.missedPongs = 0;
  }

  // Connect to WebSocket
  async connect(userId = null) {
    if (this.isConnected || this.isConnecting) {
      console.log('🔄 WebSocket already connected or connecting');
      return Promise.resolve();
    }

    // Store userId for reconnections
    if (userId) {
      this.userId = userId;
    }

    return new Promise((resolve, reject) => {
      try {
        this.isConnecting = true;
        const wsUrl = getWebSocketUrl(this.userId);
        console.log(`📡 Connecting to WebSocket: ${wsUrl}`);
        
        this.ws = new WebSocket(wsUrl);
        
        this.ws.onopen = () => {
          console.log('✅ WebSocket connected successfully');
          this.isConnected = true;
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          this.reconnectDelay = 1000;
          
          // Start heartbeat
          this.startHeartbeat();
          
          // Process any queued messages
          this.processMessageQueue();
          
          // Notify connection handlers
          this.connectionHandlers.forEach(handler => {
            try {
              handler({ type: 'connected', connectionId: this.connectionId });
            } catch (error) {
              console.error('Error in connection handler:', error);
            }
          });
          
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            console.log(`📥 WebSocket message received: ${message.type}`);
            
            // Handle connection confirmation
            if (message.type === 'connected') {
              this.connectionId = message.data.connectionId;
              console.log(`🔗 Connection ID: ${this.connectionId}`);
            }
            
            // Handle pong response (reset missed counter)
            if (message.type === 'pong' || message.action === 'pong') {
              this.missedPongs = 0;
              console.log('💓 Pong received - connection healthy');
              return; // Don't propagate pong to handlers
            }
            
            // Route message to appropriate handlers
            const handlers = this.messageHandlers.get(message.type) || [];
            handlers.forEach(handler => {
              try {
                handler(message);
              } catch (error) {
                console.error(`Error in message handler for ${message.type}:`, error);
              }
            });
            
            // Also call generic message handlers
            const genericHandlers = this.messageHandlers.get('*') || [];
            genericHandlers.forEach(handler => {
              try {
                handler(message);
              } catch (error) {
                console.error('Error in generic message handler:', error);
              }
            });
            
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
            console.log('Raw message:', event.data);
          }
        };

        this.ws.onerror = (error) => {
          console.error('❌ WebSocket error:', error);
          this.errorHandlers.forEach(handler => {
            try {
              handler(error);
            } catch (handlerError) {
              console.error('Error in error handler:', handlerError);
            }
          });
        };

        this.ws.onclose = (event) => {
          console.log(`🔌 WebSocket closed: ${event.code} - ${event.reason}`);
          this.isConnected = false;
          this.isConnecting = false;
          this.connectionId = null;
          
          // Stop heartbeat
          this.stopHeartbeat();
          
          // Notify connection handlers
          this.connectionHandlers.forEach(handler => {
            try {
              handler({ 
                type: 'disconnected', 
                code: event.code, 
                reason: event.reason 
              });
            } catch (error) {
              console.error('Error in disconnection handler:', error);
            }
          });
          
          // Auto-reconnect if not a clean close
          if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect();
          } else if (this.isConnecting) {
            // Connection failed during initial connect
            reject(new Error(`WebSocket connection failed: ${event.code} - ${event.reason}`));
          }
        };

        // Connection timeout
        setTimeout(() => {
          if (this.isConnecting) {
            console.log('⏰ WebSocket connection timeout');
            this.ws.close();
            reject(new Error('WebSocket connection timeout'));
          }
        }, 10000); // 10 second timeout

      } catch (error) {
        console.error('❌ Error creating WebSocket connection:', error);
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  // Schedule reconnection with exponential backoff
  scheduleReconnect() {
    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000);
    
    console.log(`🔄 Scheduling reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);
    
    setTimeout(() => {
      if (!this.isConnected && this.reconnectAttempts <= this.maxReconnectAttempts) {
        console.log(`🔄 Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
        this.connect().catch(error => {
          console.error('Reconnection failed:', error);
        });
      }
    }, delay);
  }

  // Disconnect WebSocket
  disconnect() {
    if (this.ws) {
      console.log('🔌 Disconnecting WebSocket');
      this.stopHeartbeat();
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    this.isConnected = false;
    this.isConnecting = false;
    this.connectionId = null;
    this.reconnectAttempts = 0;
  }

  // Start heartbeat to keep connection alive
  startHeartbeat() {
    this.stopHeartbeat();
    this.missedPongs = 0;
    console.log('💓 Starting heartbeat (5 minute interval)');
    
    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected && this.ws) {
        try {
          // Check if too many pongs missed
          if (this.missedPongs >= 3) {
            console.error('❌ Connection appears dead (3 missed pongs), reconnecting...');
            this.ws.close(4000, 'Heartbeat timeout');
            return;
          }
          
          this.ws.send(JSON.stringify({ action: 'ping' }));
          this.missedPongs++;
          console.log('💓 Heartbeat ping sent');
        } catch (error) {
          console.error('❌ Error sending heartbeat:', error);
        }
      }
    }, 5 * 60 * 1000); // Every 5 minutes
  }

  // Stop heartbeat
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
      this.missedPongs = 0;
      console.log('💓 Heartbeat stopped');
    }
  }

  // Send message to WebSocket
  sendMessage(message) {
    if (!this.isConnected) {
      console.log('📋 Queueing message (not connected):', message.action);
      this.messageQueue.push(message);
      
      // Try to connect if not already connecting
      if (!this.isConnecting) {
        this.connect().catch(error => {
          console.error('Failed to connect for message sending:', error);
        });
      }
      return;
    }

    try {
      const messageStr = JSON.stringify(message);
      console.log(`📤 Sending WebSocket message: ${message.action}`);
      this.ws.send(messageStr);
    } catch (error) {
      console.error('❌ Error sending WebSocket message:', error);
      throw error;
    }
  }

  // Process queued messages
  processMessageQueue() {
    if (this.messageQueue.length > 0) {
      console.log(`📋 Processing ${this.messageQueue.length} queued messages`);
      const messages = [...this.messageQueue];
      this.messageQueue = [];
      
      messages.forEach(message => {
        try {
          this.sendMessage(message);
        } catch (error) {
          console.error('Error sending queued message:', error);
        }
      });
    }
  }

  // Send chat message
  sendChatMessage(message, mode = 'bedrock-agent', sessionId = null, messageId = null) {
    // Generate or reuse session ID
    if (!sessionId) {
      if (!this.sessionId) {
        this.sessionId = `ws_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      }
      sessionId = this.sessionId;
    } else {
      this.sessionId = sessionId;
    }

    // Generate messageId if not provided (format: sessionId_timestamp for backend compatibility)
    if (!messageId) {
      const timestamp = Date.now();
      messageId = `ws_${sessionId}_${timestamp}`;
    }

    const chatMessage = {
      action: 'chat',
      message: message,
      mode: mode,
      sessionId: sessionId,
      messageId: messageId, // Include messageId for feedback support
      timestamp: new Date().toISOString()
    };

    this.sendMessage(chatMessage);
    return sessionId;
  }

  // Add message handler
  onMessage(messageType, handler) {
    if (!this.messageHandlers.has(messageType)) {
      this.messageHandlers.set(messageType, []);
    }
    this.messageHandlers.get(messageType).push(handler);
    
    // Return unsubscribe function
    return () => {
      const handlers = this.messageHandlers.get(messageType);
      if (handlers) {
        const index = handlers.indexOf(handler);
        if (index > -1) {
          handlers.splice(index, 1);
        }
      }
    };
  }

  // Add connection handler
  onConnection(handler) {
    this.connectionHandlers.add(handler);
    
    // Return unsubscribe function
    return () => {
      this.connectionHandlers.delete(handler);
    };
  }

  // Add error handler
  onError(handler) {
    this.errorHandlers.add(handler);
    
    // Return unsubscribe function
    return () => {
      this.errorHandlers.delete(handler);
    };
  }

  // Remove all handlers
  clearHandlers() {
    this.messageHandlers.clear();
    this.connectionHandlers.clear();
    this.errorHandlers.clear();
  }

  // Get connection status
  getStatus() {
    return {
      isConnected: this.isConnected,
      isConnecting: this.isConnecting,
      connectionId: this.connectionId,
      sessionId: this.sessionId,
      reconnectAttempts: this.reconnectAttempts,
      queuedMessages: this.messageQueue.length
    };
  }

  // Reset session (for new conversations)
  resetSession() {
    this.sessionId = null;
    console.log('🔄 WebSocket session reset');
  }
}

// Create singleton instance
const webSocketService = new WebSocketService();

export default webSocketService;